/**
 * 자료이관 > 더존G20 > 분개장
 * 시흥(ENT_CD=200) 2013~2022 G20 분개장을 올려 변환 후 aslpmst/aslpdtl/aslpviw INSERT SQL을 받는다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getJournalStore,
  parseG20JournalWorkbook,
  filterSourceRows,
  sliceForView,
  convertJournalRows,
  downloadConvertedSql,
  downloadG20JournalTemplate,
  sourceGridColumns,
  MIG_ENT_CD,
  MIG_YEAR_FROM,
  MIG_YEAR_TO,
} from 'migrate/g20JournalConvert';

const GRID_ID = 'mig-g20-journal-src';

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 4px 2px 6px;
  flex-shrink: 0;
`;

const GridWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

function formatAmt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('ko-KR');
}

function G20JournalConvertInner() {
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [hint, setHint] = useState(
    `시흥 ENT_CD=${MIG_ENT_CD} · ${MIG_YEAR_FROM}~${MIG_YEAR_TO}년 분개장(여러 파일)을 업로드하세요. 변환 결과는 INSERT SQL입니다.`
  );
  const [busy, setBusy] = useState(false);
  const [colRev, setColRev] = useState(0);

  const searchDefaultValues = useMemo(
    () => ({
      tenantCode,
      fiscalYear: '',
      keyword: '',
      pageSize: String(DEFAULT_PAGE_SIZE),
      page: 1,
    }),
    [tenantCode]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaultValues });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const columns = useMemo(() => sourceGridColumns(hiCol), [colRev]);

  const loadWindow = useCallback(() => {
    const store = getJournalStore();
    const values = getValues() || {};
    const filtered = filterSourceRows(store.sourceRows, values);
    const win = sliceForView(filtered, values.page, values.pageSize);
    if (win.page !== Number(values.page)) setValue('page', win.page);
    const fileBit = store.fileName ? `파일 ${store.fileName}` : '미업로드';
    const skipBit = store.skipped ? ` · 월계/누계 제외 ${formatAmt(store.skipped)}건` : '';
    const accBit = store.accMaster?.size
      ? ` · 계정마스터 ${formatAmt(store.accMaster.size)}건(${store.accMasterSource || 'aaccmst'})`
      : '';
    const colBit = store.sourceColDefs?.length ? ` · 원본컬럼 ${formatAmt(store.sourceColDefs.length)}개` : '';
    setHint(
      `${fileBit} · ENT_CD=${MIG_ENT_CD} · 전체 ${formatAmt(win.total)}건${skipBit}${accBit}${colBit}` +
        (win.total
          ? ` · 화면 ${formatAmt(win.start + 1)}–${formatAmt(win.end)} · ${win.page}/${win.totalPages}페이지`
          : '')
    );
    return win.viewRows;
  }, [getValues, setValue]);

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    customOnSearch: async () => loadWindow(),
  });

  const refreshWindow = useCallback(async () => {
    await grid.handleSearch();
  }, [grid]);

  // 컬럼 구성이 바뀌면 HiGrid를 remount 하므로, 그 다음에 화면 분량을 다시 넣는다.
  useEffect(() => {
    if (colRev === 0) return;
    refreshWindow().catch((e) => showAlert(e.message || '조회 실패', 'error'));
    // refreshWindow는 grid 객체 식별자가 매 렌더 바뀔 수 있어 colRev만 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colRev]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '분개장(화면)',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: 'G20분개장_화면' });

  const handleSearch = useCallback(
    async (opts = {}) => {
      const store = getJournalStore();
      if (!store.sourceRows.length) {
        if (!opts.silent) showAlert('먼저 분개장 엑셀을 업로드하세요.', 'warning');
        return;
      }
      setValue('page', 1);
      try {
        await refreshWindow();
      } catch (e) {
        showAlert(e.message || '조회 실패', 'error');
      }
    },
    [setValue, refreshWindow]
  );

  const handlePage = useCallback(
    async (delta) => {
      const store = getJournalStore();
      if (!store.sourceRows.length) {
        showAlert('먼저 분개장 엑셀을 업로드하세요.', 'warning');
        return;
      }
      const values = getValues() || {};
      const filtered = filterSourceRows(store.sourceRows, values);
      const cur = sliceForView(filtered, values.page, values.pageSize);
      const next = Math.min(cur.totalPages, Math.max(1, cur.page + delta));
      if (next === cur.page) return;
      setValue('page', next);
      try {
        await refreshWindow();
      } catch (e) {
        showAlert(e.message || '조회 실패', 'error');
      }
    },
    [getValues, setValue, refreshWindow]
  );

  const handleTemplate = useCallback(() => {
    downloadG20JournalTemplate();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadFile = useCallback(
    async (e) => {
      const files = [...(e.target.files || [])];
      e.target.value = '';
      if (!files.length) return;
      setBusy(true);
      grid.setLoading?.(true);
      try {
        let journalFiles = 0;
        let accMsg = '';
        const warns = [];
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          setHint(`엑셀 읽는 중… (${i + 1}/${files.length}) ${file.name}`);
          const buf = await file.arrayBuffer();
          const parsed = await parseG20JournalWorkbook(buf, {
            tenantCode: MIG_ENT_CD,
            fileName: file.name,
            append: journalFiles > 0,
            onProgress: ({ loaded, totalHint }) => {
              if (totalHint) setHint(`엑셀 변환 중… ${file.name} ${formatAmt(loaded)} / ${formatAmt(totalHint)}`);
              else setHint(`엑셀 변환 중… ${file.name} ${formatAmt(loaded)}건`);
            },
          });
          if (parsed.kind === 'accMaster') {
            accMsg = `계정마스터 ${formatAmt(parsed.accCount)}건 (${file.name})`;
          } else {
            journalFiles += 1;
            if (parsed.warnings?.length) warns.push(...parsed.warnings);
          }
        }
        if (journalFiles) setColRev((n) => n + 1);
        const years = [
          ...new Set(
            getJournalStore()
              .sourceRows.map((r) => String(r.slipDate || '').slice(0, 4))
              .filter((y) => /^\d{4}$/.test(y))
          ),
        ].sort();
        if (years.length === 1) setValue('fiscalYear', years[0]);
        else setValue('fiscalYear', '');
        setValue('page', 1);
        setValue('keyword', '');
        const store = getJournalStore();
        const warn = warns.length ? `\n${[...new Set(warns)].join('\n')}` : '';
        const colCnt = store.sourceColDefs?.length || 0;
        showAlert(
          (journalFiles
            ? `분개장 ${journalFiles}개 파일 · 분개 ${formatAmt(store.sourceRows.length)}건 · 원본컬럼 ${formatAmt(colCnt)}개`
            : '계정마스터만 반영했습니다.') +
            (accMsg ? `\n${accMsg}` : store.accMaster?.size ? `\n계정마스터 ${formatAmt(store.accMaster.size)}건` : '') +
            (store.skipped ? `\n월계/누계 등 ${formatAmt(store.skipped)}건은 제외했습니다.` : '') +
            `\n이관 대상 ENT_CD=${MIG_ENT_CD}, ${MIG_YEAR_FROM}~${MIG_YEAR_TO}년. 변환 시 INSERT SQL을 받습니다.${warn}`,
          warns.length ? 'warning' : 'success'
        );
      } catch (err) {
        showAlert(err.message || '엑셀을 읽지 못했습니다.', 'error');
        setHint('업로드에 실패했습니다. 분개장 또는 aaccmst 양식을 확인하세요.');
      } finally {
        setBusy(false);
        grid.setLoading?.(false);
      }
    },
    [grid, setValue]
  );

  const handleConvert = useCallback(async () => {
    const store = getJournalStore();
    if (!store.sourceRows.length) {
      showAlert('먼저 분개장 엑셀을 업로드하세요. 2013~2022 파일을 여러 개 선택할 수 있습니다.', 'warning');
      return;
    }
    const values = getValues() || {};
    const filtered = filterSourceRows(store.sourceRows, {
      fiscalYear: values.fiscalYear,
      keyword: '',
      yearFrom: values.fiscalYear ? undefined : MIG_YEAR_FROM,
      yearTo: values.fiscalYear ? undefined : MIG_YEAR_TO,
    });
    if (!filtered.length) {
      showAlert(`변환할 행이 없습니다. ${MIG_YEAR_FROM}~${MIG_YEAR_TO}년 또는 조회 회계연도를 확인하세요.`, 'warning');
      return;
    }
    setBusy(true);
    grid.setLoading?.(true);
    try {
      const converted = await convertJournalRows(filtered, {
        tenantCode: MIG_ENT_CD,
        acctMap: store.acctMap,
        accMaster: store.accMaster,
        onProgress: ({ done, total }) => {
          if (total) setHint(`변환 중… ${formatAmt(done)} / ${formatAmt(total)}`);
        },
      });
      const s = converted.summary;
      setHint('INSERT SQL 작성 중…');
      const built = await downloadConvertedSql(converted, '', ({ done, total }) => {
        if (total) setHint(`SQL 작성 중… ${formatAmt(done)} / ${formatAmt(total)}`);
      });
      await refreshWindow();
      const warn =
        s.unbalanced > 0 ? `\n차대 불일치 전표 ${formatAmt(s.unbalanced)}건 — SQL 헤더 주석을 확인하세요.` : '';
      const miss = s.missingAccCnt
        ? `\n계정마스터 미매핑 ${formatAmt(s.missingAccCnt)}건: ${(s.missingAcc || []).slice(0, 8).join(', ')}${s.missingAccCnt > 8 ? '…' : ''}`
        : '';
      showAlert(
        `변환 완료 (${built.fileName})\naslpmst ${formatAmt(s.slipCnt)} · aslpdtl ${formatAmt(s.lineCnt)} · aslpviw ${formatAmt((converted.views || []).length)}\nENT_CD=${MIG_ENT_CD} · 차변 ${formatAmt(s.totalDr)} · 대변 ${formatAmt(s.totalCr)}${warn}${miss}`,
        s.unbalanced || s.missingAccCnt ? 'warning' : 'success'
      );
    } catch (err) {
      showAlert(err.message || '변환에 실패했습니다.', 'error');
    } finally {
      setBusy(false);
      grid.setLoading?.(false);
    }
  }, [getValues, grid, refreshWindow]);

  const actionButtons = useMemo(
    () => [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: () => handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
        hotkey: 'F7',
        disabled: busy,
      },
      excelButton,
    ],
    [handleSearch, excelButton, busy]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'tpl',
        label: '양식다운로드',
        icon: '📄',
        onClick: handleTemplate,
        title: '더존 G20 분개장(기표 2단 헤더·dump 컬럼)',
        disabled: busy,
      },
      {
        id: 'upload',
        label: '엑셀업로드',
        icon: '📤',
        onClick: handleUploadClick,
        title: '2013~2022 분개장 여러 파일 + aaccmst',
        disabled: busy,
      },
      {
        id: 'prev',
        label: '이전',
        icon: '◀',
        onClick: () => handlePage(-1),
        title: '이전 페이지',
        disabled: busy,
      },
      {
        id: 'next',
        label: '다음',
        icon: '▶',
        onClick: () => handlePage(1),
        title: '다음 페이지',
        disabled: busy,
      },
      {
        id: 'convert',
        label: '변환',
        icon: '🔁',
        onClick: () => handleConvert().catch((e) => showAlert(e.message || '변환 실패', 'error')),
        title: 'aslpmst / aslpdtl / aslpviw INSERT SQL 다운로드',
        variant: 'success',
        disabled: busy,
      },
    ],
    [handleTemplate, handleUploadClick, handlePage, handleConvert, busy]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'select',
          options: [
            { value: '', label: '전체' },
            ...Array.from({ length: 14 }, (_, i) => {
              const y = String(2013 + i);
              return { value: y, label: y };
            }),
          ],
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'keyword',
          label: '검색어',
          type: 'text',
          width: '200px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '전표·계정·적요·거래처',
        },
        {
          name: 'pageSize',
          label: '화면건수',
          type: 'select',
          options: PAGE_SIZE_OPTIONS,
          width: '110px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup
          buttons={actionButtons}
          extraButtons={extraButtons}
          loading={busy || grid.loading}
        />
        <CollapsibleSearchPanel
          storageKey="mig-g20-journal"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea compact fields={searchFields} onFieldEnter={() => handleSearch({ silent: true })} />
        </CollapsibleSearchPanel>
      </FormProvider>
      <Meta>{hint}</Meta>
      <GridWrap>
        <GridExportFocusScope gridId={GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            key={colRev}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu={false}
            showModColumn={false}
            enableCut={false}
            enableFindReplace
            allowReplace={false}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            useVirtualRows
            exportFileName="G20분개장_화면"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
          />
        </GridExportFocusScope>
      </GridWrap>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        multiple
        style={{ display: 'none' }}
        onChange={handleUploadFile}
      />
      {excelDialog}
    </PageContainer>
  );
}

const G20JournalConvert = () => (
  <GridExportProvider>
    <G20JournalConvertInner />
  </GridExportProvider>
);

export default G20JournalConvert;
